import { Route, Routes, useLocation } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { lazy, useEffect, useState } from "react";
import { languages } from "./assets/languages/languages";
import AppContext from "./context/AppContext";
import RequireAuth from "./customHooks/RequireAuth";
import { ToastContainer } from "react-toastify";
import { ChatProvider } from "./context/ChatProvider";
import rtlPlugin from "stylis-plugin-rtl";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import { OnlineRequestProvider } from "./context/OnlineRequestContext";
import { decrypt } from "./reusable/crypto";
import "react-toastify/dist/ReactToastify.css";
import "./assets/css/app.css";
import "./layouts/main.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import BackDrop from "./components/backDrop/BackDrop";
import Analytics from "./Analytics";
import { loadConfig } from "./reusable/functions";
import { config } from "./reusable/utils";
import AboutUs from "./pages/aboutus/AboutUs";
import RegistrationAgent from "./pages/registrationAgent/RegistrationAgent";

// import { getCookie, setCookie } from "./reusable/functions";
const SubmitComplain = lazy(() => import("./pages/complain/SubmitComplain"));
const DeviceStatus = lazy(() => import("./pages/deviceStatus/DeviceStatus"));
const LandingParent = lazy(() => import("./layouts/landing/LandingParent"));
const LandingParentAe = lazy(
  () => import("./layouts/landingAE/landingAE/LandingParent"),
);
const OnlineRequest = lazy(() => import("./pages/onlineRequest/OnlineRequest"));
const Landing = lazy(() => import("./layouts/Landing"));
const LandingAe = lazy(() => import("./layouts/landingAE/Landing"));
const Home = lazy(() => import("./pages/home/Home"));
const ContactUs = lazy(() => import("./pages/contactus/ContactUs"));
const Pagenotfound = lazy(() => import("./components/404/Pagenotfound"));
const NeshanServicePlace = lazy(
  () => import("./pages/servicePlace/NeshanServicePlace"),
);
const ServicePlace = lazy(() => import("./pages/servicePlace/ServicePlace"));
const Main = lazy(() => import("./layouts/Main"));
const FollowUp = lazy(() => import("./pages/followup/FollowUp"));
// const PurchaseSaleList = lazy(() =>
//   import("./pages/PurchaseSaleList/PurchaseSaleList")
// );

function App() {
  const [app, setApp] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentLanguageCode =
    localStorage.getItem("i18nextLng") || process.env.REACT_APP_DEFAULTLNG;
  const currentLanguage = languages.find((l) => l.code === currentLanguageCode);
  const theme = createTheme({
    direction: currentLanguage.dir ? "rtl" : "ltr",
    typography: {
      fontFamily: "'Estedad', 'ui-sans-serif', 'system-ui', sans-serif",
      fontWeightLight: 300, // Tailwind Light
      fontWeightRegular: 400, // Tailwind Regular
      fontWeightMedium: 500, // Tailwind Medium
      fontWeightBold: 700, // Tailwind Bold
    },
  });

  useEffect(() => {
    const getUser = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const user = searchParams.get("user");

      if (user) {
        try {
          const formattedUser = user.replace(/\s/g, "+").replace(/\n/g, "+");
          const decryptedUser = decrypt(formattedUser);
          const { token, ...userInfo } = decryptedUser;

          if (token) {
            localStorage.setItem(process.env.REACT_APP_TOKENNAME, token);
            localStorage.setItem("userInfo", JSON.stringify(userInfo));
          }
          //***Remove user param */
          const newUrl =
            window.location.pathname +
            window.location.search
              .replace(/(\?|&)user=[^&]*/, "")
              .replace(/^&/, "?");
          window.history.replaceState({}, "", newUrl);
        } catch (error) {
          console.error("Decryption failed:", error);
        }
      }

      setLoading(false); // Set loading to false after the user has been processed
    };
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getlocation = async () => {
    const onSuccess = (location) => {
      setApp((prev) => ({
        ...prev,
        userLocation: {
          lat: location.coords.latitude,
          lng: location.coords.longitude,
        },
      }));
    };

    await navigator?.geolocation?.getCurrentPosition(onSuccess);
  };
  const checkVersion = async () => {
    try {
      const response = await fetch("/version.json");
      const data = await response.json();
      const fetchedLatestVersion = data.version;
      localStorage.setItem("appVersion", fetchedLatestVersion);
    } catch (error) {
      console.error("Failed to fetch version:", error);
    }
  };

  useEffect(() => {
    document.body.dir = currentLanguage.dir || "ltr";
    document.documentElement.setAttribute("lang", currentLanguage.code);

    if (config.isAe && mapboxgl.getRTLTextPluginStatus() === "unavailable") {
      mapboxgl.setRTLTextPlugin(
        "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js",
        null,
        true, // Lazy load the plugin
      );
    }
    checkVersion();
    getlocation();
    loadConfig().then((AppConfig) =>
      setApp((prev) => ({
        ...prev,
        lang: currentLanguage.code,
        langCode: currentLanguage.no,
        dir: currentLanguage?.dir,
        shortForm: currentLanguage?.shortForm,
        isAe: config.isAe,
        AppConfig,
      })),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create rtl cache
  const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
  });
  const cacheLtr = createCache({
    key: "muiltr",
  });

  if (loading) {
    return <BackDrop open={loading} />;
  }

  const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, [pathname]);

    return null;
  };

  return (
    <CacheProvider value={currentLanguage.dir ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={theme}>
        <AppContext.Provider value={{ app, setApp }}>
          <OnlineRequestProvider>
            <ChatProvider>
              <Analytics />
              <ScrollToTop />
              <Routes>
                <Route element={<RequireAuth />}>
                  <Route element={<Main />}>
                    <Route path="Home" element={<Home />} />
                    <Route path="RequestOnline" element={<OnlineRequest />} />
                    {/* <Route path="PurchaseList" element={<PurchaseSaleList />} />
                    <Route path="SaleList" element={<PurchaseSaleList />} /> */}
                    <Route path="Support" element={<ContactUs />} />
                    <Route path="DeviceStatus" element={<DeviceStatus />} />
                    <Route path="FollowUp" element={<FollowUp />} />
                    <Route path="Complain" element={<SubmitComplain />} />
                    {app?.AppConfig?.MAP === "MAPBOX" ? (
                      <Route path="ServicePlace" element={<ServicePlace />} />
                    ) : (
                      <Route
                        path="ServicePlace"
                        element={<NeshanServicePlace />}
                      />
                    )}
                  </Route>
                </Route>

                {config.isAe ? (
                  <Route path="/" element={<LandingAe />}>
                    <Route path="/" element={<LandingParentAe />} />
                    <Route path="ContactUs" element={<ContactUs />} />
                    <Route path="AboutUs" element={<AboutUs />} />
                    <Route path="RegistrationAgent" element={<RegistrationAgent />} />
                  </Route>
                ) : (
                  <Route path="/" element={<Landing />}>
                    <Route path="/" element={<LandingParent />} />
                    <Route path="ContactUs" element={<ContactUs />} />
                    <Route path="AboutUs" element={<AboutUs />} />
                    <Route path="RegistrationAgent" element={<RegistrationAgent />} />
                  </Route>
                )}
                <Route path="*" element={<Pagenotfound />} />
              </Routes>
              <ToastContainer rtl={currentLanguage.dir ? true : false} />
            </ChatProvider>
          </OnlineRequestProvider>
        </AppContext.Provider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
